<template>
  <div class="home-page">
    <Header />
    
    <div class="carousel">
      <div class="particles-container">
        <div class="particle" v-for="n in 50" :key="n" 
             :style="{
               left: Math.random() * 100 + '%',
               top: Math.random() * 100 + '%',
               animationDelay: Math.random() * 15 + 's',
               animationDuration: (10 + Math.random() * 20) + 's'
             }">
        </div>
      </div>
      <div class="glowing-lines">
        <div class="glowing-line" v-for="n in 5" :key="n"></div>
      </div>
      <div class="digital-grid"></div>
      <div class="carousel-inner">
        <div 
          v-for="(slide, index) in slides" 
          :key="index" 
          class="carousel-item" 
          :class="{ 
            active: currentSlide === index,
            'prev': (currentSlide === index + 1) || (currentSlide === 0 && index === slides.length - 1),
            'next': (currentSlide === index - 1) || (currentSlide === slides.length - 1 && index === 0)
          }"
          :style="{
            backgroundImage: `url(${slide.image})`,
            zIndex: currentSlide === index ? 10 : 1,
            display: isVisible(index) ? 'block' : 'none'
          }"
        >
          <div class="parallax-bg" :style="{ backgroundImage: `url(${slide.image})` }"></div>
          <div class="carousel-overlay">
            <div class="carousel-content">
              <div class="glitch-container">
                <h1 class="animate-title glitch-text" :data-text="slide.title">{{ slide.title }}</h1>
                <div class="glitch-effect"></div>
              </div>
              <p class="animate-description">{{ slide.description }}</p>
              <router-link to="/contact" class="carousel-btn animate-btn">
                <span class="btn-text">联系我们</span>
                <span class="btn-shine"></span>
                <span class="btn-glow"></span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
      
      <div class="carousel-indicators">
        <div 
          v-for="(slide, index) in slides" 
          :key="index" 
          class="carousel-indicator" 
          :class="{ active: currentSlide === index }"
          @click="setCurrentSlide(index)"
        >
          <span class="indicator-pulse"></span>
        </div>
      </div>
      
      <div class="carousel-controls">
        <div class="carousel-control" @click="prevSlide">
          <i class="bi bi-chevron-left"></i>
          <div class="control-ripple"></div>
        </div>
        <div class="carousel-control" @click="nextSlide">
          <i class="bi bi-chevron-right"></i>
          <div class="control-ripple"></div>
        </div>
      </div>
    </div>
    
    <!-- 核心优势概览 -->
    <section class="highlights-section">
      <div class="container">
        <div class="section-title">
          <h2>俊采优势</h2>
          <div class="divider"></div>
          <p>专业的人做专业的事</p>
        </div>
        
        <div class="highlights-grid">
          <div class="highlight-card">
            <div class="highlight-icon">
              <i class="bi bi-database-check"></i>
            </div>
            <h3>海量人才库</h3>
            <p>精选人才资源</p>
          </div>
          
          <div class="highlight-card">
            <div class="highlight-icon">
              <i class="bi bi-people-fill"></i>
            </div>
            <h3>专业顾问团队</h3>
            <p>行业资深顾问</p>
          </div>
          
          <div class="highlight-card">
            <div class="highlight-icon">
              <i class="bi bi-lightning-charge-fill"></i>
            </div>
            <h3>高效匹配</h3>
            <p>精准推荐合适人选</p>
          </div>
          
          <div class="highlight-card">
            <div class="highlight-icon">
              <i class="bi bi-award"></i>
            </div>
            <h3>品质保障</h3>
            <p>值得信赖的服务品质</p>
          </div>
        </div>
        
        <div class="text-center mt-5">
          <router-link to="/about" class="learn-more-btn">
            了解更多 <i class="bi bi-arrow-right"></i>
          </router-link>
        </div>
      </div>
    </section>
    
    <!-- 业务领域概览 -->
    <section class="services-section">
      <div class="container">
        <div class="section-title">
          <h2>业务领域</h2>
          <div class="divider"></div>
          <p>为您提供全方位的猎头服务</p>
        </div>
        
        <div class="services-grid">
          <div class="service-card">
            <div class="service-image"></div>
            <div class="service-content">
              <h3>物流供应链猎头</h3>
              <p>为物流、供应链、货代、航运等行业提供专业的人才招聘服务</p>
              <router-link to="/business" class="service-link">了解更多 <i class="bi bi-arrow-right"></i></router-link>
            </div>
          </div>
          
          <div class="service-card">
            <div class="service-image"></div>
            <div class="service-content">
              <h3>互联网电子商务猎头</h3>
              <p>为互联网、电子商务、IT等行业提供专业的人才招聘服务</p>
              <router-link to="/business" class="service-link">了解更多 <i class="bi bi-arrow-right"></i></router-link>
            </div>
          </div>
          
          <div class="service-card">
            <div class="service-image"></div>
            <div class="service-content">
              <h3>新能源电力猎头</h3>
              <p>为新能源、电力、电器等行业提供专业的人才招聘服务</p>
              <router-link to="/business" class="service-link">了解更多 <i class="bi bi-arrow-right"></i></router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- 客户案例概览 -->
    <section class="clients-preview-section">
      <div class="container">
        <div class="section-title">
          <h2>成功案例</h2>
          <div class="divider"></div>
          <p>我们已为众多知名企业提供专业服务</p>
        </div>
        
        <div class="clients-box">
          <ul class="clients-items">
            <li class="client-item cctv" :class="{ active: hoveredClient === 'cctv' }" @mouseenter="hoveredClient = 'cctv'" @mouseleave="hoveredClient = null"></li>
            <li class="client-item unicom" :class="{ active: hoveredClient === 'unicom' }" @mouseenter="hoveredClient = 'unicom'" @mouseleave="hoveredClient = null"></li>
            <li class="client-item tsinghua" :class="{ active: hoveredClient === 'tsinghua' }" @mouseenter="hoveredClient = 'tsinghua'" @mouseleave="hoveredClient = null"></li>
            <li class="client-item apple" :class="{ active: hoveredClient === 'apple' }" @mouseenter="hoveredClient = 'apple'" @mouseleave="hoveredClient = null"></li>
          </ul>
        </div>
        
        <div class="text-center mt-5">
          <button type="button" class="learn-more-btn" @click="goToPartners">
            查看更多客户 <i class="bi bi-arrow-right"></i>
          </button>
        </div>
      </div>
    </section>
    
    <!-- 联系我们 CTA -->
    <section class="cta-section">
      <div class="cta-overlay"></div>
      <div class="container">
        <div class="cta-content">
          <h2>需要专业的猎头服务？</h2>
          <p>无论您是需要招聘高级管理人才，还是寻找专业技术人才，俊采猎头都能为您提供最专业的服务。</p>
          <router-link to="/contact" class="cta-btn">立即联系我们</router-link>
        </div>
      </div>
    </section>
    
    <Footer />
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import Header from '@/components/Header.vue';
import Footer from '@/components/Footer.vue';

export default {
  name: 'HomePage',
  components: {
    Header,
    Footer
  },
  setup() {
    const router = useRouter();
    const currentSlide = ref(0);
    const autoplayInterval = ref(null);
    const hoveredClient = ref(null);

    // 跳转到客户案例页并平滑滚动到合作伙伴区块
    const goToPartners = () => {
      router.push('/clients').then(() => {
        setTimeout(() => {
          document.getElementById('partners')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 400);
      });
    };
    
    const slides = [
      {
        title: "俊采猎头",
        description: "专业的人做专业的事",
        image: require('@/assets/wallpaper1_1.jpg')
      },
      {
        title: "厚积薄发",
        description: "登上峰顶，不是为了饱览风光，是为了寻找更高的山峰",
        image: require('@/assets/wallpaper1_2.jpg')
      },
      {
        title: "专业服务",
        description: "物流供应链、互联网电子商务、新能源电力等行业的专业猎头服务",
        image: require('@/assets/wallpaper1_3.jpg')
      },
      {
        title: "成为您的最佳合作伙伴",
        description: "俊采猎头，努力成为客户人力资源管理领域最好的合作伙伴",
        image: require('@/assets/wallpaper1_4.jpg')
      }
    ];
    
    const nextSlide = () => {
      currentSlide.value = (currentSlide.value + 1) % slides.length;
    };
    
    const prevSlide = () => {
      currentSlide.value = (currentSlide.value - 1 + slides.length) % slides.length;
    };
    
    const setCurrentSlide = (index) => {
      currentSlide.value = index;
      resetAutoplay();
    };
    
    const startAutoplay = () => {
      autoplayInterval.value = setInterval(() => {
        nextSlide();
      }, 5000);
    };
    
    const resetAutoplay = () => {
      if (autoplayInterval.value) {
        clearInterval(autoplayInterval.value);
        startAutoplay();
      }
    };
    
    const isVisible = (index) => {
      return currentSlide.value === index || 
             (currentSlide.value === index + 1) || 
             (currentSlide.value === 0 && index === slides.length - 1) ||
             (currentSlide.value === index - 1) || 
             (currentSlide.value === slides.length - 1 && index === 0);
    };
    
    const handleMouseMove = (e) => {
      const carousel = document.querySelector('.carousel');
      if (!carousel) return;
      
      const { left, top, width, height } = carousel.getBoundingClientRect();
      const x = (e.clientX - left) / width - 0.5;
      const y = (e.clientY - top) / height - 0.5;
      
      const content = document.querySelector('.carousel-content');
      if (content) {
        content.style.transform = `translate3d(${x * 20}px, ${y * 20}px, 0)`;
      }
      
      const parallaxBgs = document.querySelectorAll('.parallax-bg');
      parallaxBgs.forEach(bg => {
        bg.style.transform = `translate3d(${x * -30}px, ${y * -30}px, 0)`;
      });
    };
    
    onMounted(() => {
      startAutoplay();
      document.querySelector('.carousel').addEventListener('mousemove', handleMouseMove);
    });
    
    onBeforeUnmount(() => {
      if (autoplayInterval.value) {
        clearInterval(autoplayInterval.value);
      }
      document.querySelector('.carousel')?.removeEventListener('mousemove', handleMouseMove);
    });
    
    return {
      currentSlide,
      slides,
      goToPartners,
      nextSlide,
      prevSlide,
      setCurrentSlide,
      isVisible,
      hoveredClient
    };
  }
}
</script>

<style scoped>
.home-page {
  overflow-x: hidden;
  perspective: 1000px;
}

.carousel {
  height: 500px;
  position: relative;
  margin-top: 70px;
  background-color: #000;
  overflow: hidden;
  transform-style: preserve-3d;
}

.particles-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 15;
  opacity: 0.8;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  box-shadow: 0 0 12px 3px rgba(0, 150, 136, 0.7);
  animation: floatParticle 15s linear infinite;
}

.particle:nth-child(odd) {
  background-color: rgba(0, 150, 136, 0.7);
  box-shadow: 0 0 12px 3px rgba(255, 255, 255, 0.7);
}

.particle:nth-child(3n) {
  width: 5px;
  height: 5px;
}

.particle:nth-child(5n) {
  width: 3px;
  height: 3px;
}

@keyframes floatParticle {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translate(120px, 120px) scale(0.2);
    opacity: 0;
  }
}

.glowing-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
  opacity: 0.6;
}

.glowing-line {
  position: absolute;
  width: 1px;
  height: 100%;
  background: linear-gradient(to bottom, 
    rgba(0, 150, 136, 0), 
    rgba(0, 150, 136, 0.7), 
    rgba(0, 150, 136, 0));
  animation: moveLine 15s linear infinite;
  opacity: 0.6;
}

.glowing-line:nth-child(1) {
  left: 20%;
  animation-delay: 0s;
}

.glowing-line:nth-child(2) {
  left: 40%;
  animation-delay: 3s;
}

.glowing-line:nth-child(3) {
  left: 60%;
  animation-delay: 6s;
}

.glowing-line:nth-child(4) {
  left: 80%;
  animation-delay: 9s;
}

.glowing-line:nth-child(5) {
  left: 10%;
  animation-delay: 12s;
}

@keyframes moveLine {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
}

.carousel-inner {
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
}

.carousel-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  opacity: 0;
  transform: scale(1.1) translateZ(-50px);
  transition: opacity 0.8s cubic-bezier(0.165, 0.84, 0.44, 1), 
              transform 1s cubic-bezier(0.165, 0.84, 0.44, 1);
  display: none;
  will-change: transform, opacity;
  filter: saturate(1.3) contrast(1.2);
}

.carousel-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(0, 77, 64, 0.3) 0%, rgba(0, 0, 0, 0) 50%);
  z-index: 2;
}

.carousel-item.prev {
  display: block;
  opacity: 0.3;
  transform: translateX(-80px) scale(0.95);
  transform-origin: left center;
  z-index: 5;
  filter: saturate(0.8) blur(2px);
}

.carousel-item.next {
  display: block;
  opacity: 0.3;
  transform: translateX(80px) scale(0.95);
  transform-origin: right center;
  z-index: 5;
  filter: saturate(0.8) blur(2px);
}

.carousel-item.active {
  opacity: 1;
  transform: translateX(0) scale(1) translateZ(0);
  display: block;
  animation: subtleZoom 20s linear infinite alternate;
  filter: saturate(1.3) contrast(1.2);
}

@keyframes subtleZoom {
  0% {
    background-position: center;
    transform: scale(1);
  }
  100% {
    background-position: center 52%;
    transform: scale(1.08);
  }
}

.carousel-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.7));
  display: flex;
  align-items: center;
  justify-content: center;
  transform-style: preserve-3d;
  z-index: 3;
}

.carousel-content {
  text-align: center;
  color: #fff;
  max-width: 800px;
  padding: 0 20px;
  transform-style: preserve-3d;
  position: relative;
  transition: transform 0.3s ease-out;
  will-change: transform;
}

.glitch-container {
  position: relative;
  display: inline-block;
  margin-bottom: 20px;
  perspective: 1000px;
}

.glitch-text {
  font-size: 46px;
  font-weight: 700;
  letter-spacing: 2px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.215, 0.610, 0.355, 1) 0.2s;
  position: relative;
  animation: textGlow 3s infinite alternate;
}

.glitch-text::before,
.glitch-text::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.3;
  display: block;
}

.glitch-text::before {
  color: #0ff;
  z-index: -1;
  animation: subtleGlitch 4s infinite alternate-reverse;
}

.glitch-text::after {
  color: #f0f;
  z-index: -2;
  animation: subtleGlitch 3s infinite alternate;
}

@keyframes textGlow {
  0%, 100% {
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    color: #fff;
  }
  50% {
    text-shadow: 0 0 15px rgba(0, 150, 136, 0.9), 0 0 30px rgba(0, 150, 136, 0.5);
    color: #e0f7fa;
  }
}

@keyframes subtleGlitch {
  0%, 100% {
    transform: translate(0);
  }
  20% {
    transform: translate(-3px, 0);
  }
  40% {
    transform: translate(3px, 0);
  }
  60% {
    transform: translate(0, 3px);
  }
  80% {
    transform: translate(3px, -3px);
  }
}

.animate-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  width: 0;
  height: 4px;
  background: linear-gradient(to right, #009688, #4CAF50);
  transform: translateX(-50%);
  transition: width 0.8s cubic-bezier(0.215, 0.610, 0.355, 1) 0.8s;
}

.carousel-item.active .animate-title::after {
  width: 120px;
  box-shadow: 0 0 15px rgba(0, 229, 255, 0.8), 0 0 30px rgba(0, 150, 136, 0.6);
}

.animate-description {
  font-size: 20px;
  margin-bottom: 30px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.215, 0.610, 0.355, 1) 0.4s;
  line-height: 1.6;
  letter-spacing: 0.5px;
}

.animate-btn {
  display: inline-block;
  padding: 14px 34px;
  background: linear-gradient(135deg, #009688, #00796b);
  color: #fff;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 600;
  letter-spacing: 1px;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.215, 0.610, 0.355, 1) 0.6s,
              background 0.3s, transform 0.3s, box-shadow 0.3s;
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.btn-text {
  position: relative;
  z-index: 2;
}

.btn-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: rotate(30deg);
  animation: subtleShine 4s infinite;
}

@keyframes subtleShine {
  0% {
    transform: rotate(30deg) translateX(-300%);
  }
  20%, 100% {
    transform: rotate(30deg) translateX(300%);
  }
}

.animate-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #00796b, #004D40);
  z-index: -1;
  transform: scaleX(0);
  transform-origin: right;
  transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.animate-btn:hover::before {
  transform: scaleX(1);
  transform-origin: left;
}

.carousel-item.active .animate-title,
.carousel-item.active .animate-description,
.carousel-item.active .animate-btn {
  opacity: 1;
  transform: translateY(0);
}

.carousel-btn:hover {
  transform: translateY(-5px) !important;
  box-shadow: 0 10px 20px rgba(0, 150, 136, 0.6), 0 0 20px rgba(0, 229, 255, 0.4);
}

.carousel-indicators {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 12px;
  z-index: 10;
}

.carousel-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  overflow: visible;
}

.indicator-pulse {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.4);
  transform: scale(1);
  opacity: 0;
}

.carousel-indicator:hover {
  background-color: rgba(255, 255, 255, 0.7);
  transform: scale(1.2);
}

.carousel-indicator:hover .indicator-pulse {
  animation: subtlePulse 1.5s infinite;
}

.carousel-indicator.active {
  background-color: #fff;
  transform: scale(1.3);
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.6), 0 0 20px rgba(0, 229, 255, 0.4);
}

.carousel-indicator.active .indicator-pulse {
  animation: subtlePulse 1.5s infinite;
}

@keyframes subtlePulse {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(3);
    opacity: 0;
  }
}

.carousel-controls {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 10;
}

.carousel-control {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  overflow: visible;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.control-ripple {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transform: scale(1);
  opacity: 0;
}

.carousel-control:hover {
  background-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
}

.carousel-control:hover .control-ripple {
  animation: subtleRipple 1.5s infinite;
}

@keyframes subtleRipple {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.carousel-control::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
  transform: scale(0);
  border-radius: 50%;
  transition: transform 0.5s;
}

.carousel-control:hover::before {
  transform: scale(1.3);
}

.carousel-control i {
  color: #fff;
  font-size: 20px;
  position: relative;
  z-index: 2;
  transition: transform 0.3s;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
}

.carousel-control:hover i {
  transform: scale(1.1);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-title {
  text-align: center;
  margin-bottom: 50px;
}

.section-title h2 {
  font-size: 28px;
  margin-bottom: 15px;
  color: #333;
}

.divider {
  width: 60px;
  height: 3px;
  background-color: #009688;
  margin: 0 auto 20px;
}

.section-title p {
  font-size: 16px;
  color: #666;
}

/* 核心优势概览样式 */
.highlights-section {
  padding: 100px 0;
  background-color: #f9f9f9;
  position: relative;
}

.highlights-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
  margin-top: 50px;
}

.highlight-card {
  background: #fff;
  border: 1px solid #eef1f0;
  border-radius: 16px;
  padding: 40px 26px;
  text-align: center;
  box-shadow: 0 4px 18px rgba(20, 40, 40, 0.05);
  transition: transform 0.4s cubic-bezier(0.165, 0.84, 0.44, 1),
              box-shadow 0.4s cubic-bezier(0.165, 0.84, 0.44, 1),
              border-color 0.4s ease;
  position: relative;
  overflow: hidden;
}

/* hover 时顶部渐变描边渐入，克制而精致 */
.highlight-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(to right, #009688, #4CAF50);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.45s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.highlight-card:hover {
  transform: translateY(-10px);
  border-color: transparent;
  box-shadow: 0 22px 44px rgba(0, 121, 107, 0.18);
}

.highlight-card:hover::before {
  transform: scaleX(1);
}

.highlight-icon {
  width: 76px;
  height: 76px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #009688 0%, #4CAF50 100%);
  border-radius: 20px;
  font-size: 32px;
  color: #fff;
  box-shadow: 0 10px 22px rgba(0, 150, 136, 0.28);
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.highlight-card:hover .highlight-icon {
  transform: translateY(-4px) rotate(-6deg);
}

.highlight-card h3 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1a2b2b;
}

/* 标题下装饰性短线，增加精致度 */
.highlight-card h3::after {
  content: '';
  display: block;
  width: 28px;
  height: 2px;
  margin: 12px auto 0;
  background: rgba(0, 150, 136, 0.35);
  border-radius: 2px;
  transition: width 0.4s ease;
}

.highlight-card:hover h3::after {
  width: 44px;
}

.highlight-card p {
  font-size: 15px;
  color: #6a7676;
  margin: 12px 0 0;
  line-height: 1.6;
}

.learn-more-btn {
  display: inline-flex;
  align-items: center;
  padding: 13px 34px;
  background: linear-gradient(135deg, #009688, #00796b);
  color: #fff;
  text-decoration: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-family: inherit;
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 8px 20px rgba(0, 150, 136, 0.22);
}

.learn-more-btn i {
  margin-left: 8px;
  transition: transform 0.3s ease;
}

.learn-more-btn:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 28px rgba(0, 150, 136, 0.35);
}

.learn-more-btn:hover i {
  transform: translateX(5px);
}

/* 客户案例概览样式 */
.clients-preview-section {
  padding: 80px 0;
  background-color: #f9f9f9;
  position: relative;
  overflow: hidden;
}

.clients-preview-section::before {
  content: '';
  position: absolute;
  top: -50px;
  right: -50px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(0, 150, 136, 0.05);
  z-index: 0;
}

.clients-preview-section::after {
  content: '';
  position: absolute;
  bottom: -80px;
  left: -80px;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: rgba(0, 150, 136, 0.05);
  z-index: 0;
}

.clients-box {
  position: relative;
  z-index: 1;
  margin: 40px auto 0;
  max-width: 960px;
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 34px rgba(20, 40, 40, 0.06);
}

.clients-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.client-item {
  position: relative;
  width: 210px;
  height: 130px;
  overflow: hidden;
  border-radius: 12px;
  z-index: 10;
  cursor: pointer;
  -webkit-transition: all .5s ease-in-out 0s;
  -moz-transition: all .5s ease-in-out 0s;
  -ms-transition: all .5s ease-in-out 0s;
  -o-transition: all .5s ease-in-out 0s;
  transition: all .5s ease-in-out 0s;
  filter: grayscale(100%) opacity(0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.client-item:hover, .client-item.active {
  transform: translateY(-5px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.15);
  filter: grayscale(0%) opacity(1);
}

.client-item::before,
.client-item::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  transition: transform 0.5s ease;
}

.client-item::after {
  transform: translateY(100%);
}

.client-item:hover::before,
.client-item.active::before {
  transform: translateY(-100%);
}

.client-item:hover::after,
.client-item.active::after {
  transform: translateY(0);
}

.client-item.cctv::before {
  background-image: url('@/assets/sprites/image0.png');
}

.client-item.cctv::after {
  background-image: url('@/assets/sprites/image15.png');
}

.client-item.unicom::before {
  background-image: url('@/assets/sprites/image1.png');
}

.client-item.unicom::after {
  background-image: url('@/assets/sprites/image16.png');
}

.client-item.tsinghua::before {
  background-image: url('@/assets/sprites/image2.png');
}

.client-item.tsinghua::after {
  background-image: url('@/assets/sprites/image17.png');
}

.client-item.apple::before {
  background-image: url('@/assets/sprites/image3.png');
}

.client-item.apple::after {
  background-image: url('@/assets/sprites/image18.png');
}

@media (max-width: 960px) {
  .clients-box {
    max-width: 700px;
  }
  
  .client-item {
    width: 200px;
    height: 130px;
    transform: scale(0.9);
    transform-origin: center center;
  }
}

@media (max-width: 767px) {
  .clients-box {
    max-width: 460px;
  }
  
  .client-item {
    width: 230px;
    height: 150px;
    transform: scale(1);
    transform-origin: center center;
  }
}

@media (max-width: 480px) {
  .clients-box {
    max-width: 230px;
  }
  
  .client-item {
    width: 180px;
    height: 120px;
    transform: scale(0.75);
    transform-origin: center center;
    margin: 0 auto 15px;
  }
}

/* 服务板块样式 */
.services-section {
  padding: 100px 0;
  background-color: #fff;
  position: relative;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 50px;
}

.service-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  position: relative;
  height: 400px;
  display: flex;
  flex-direction: column;
}

.service-card:hover {
  transform: translateY(-15px);
  box-shadow: 0 15px 35px rgba(0, 150, 136, 0.2);
}

.service-image {
  height: 200px;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
}

.service-card:nth-child(1) .service-image {
  background-image: url('@/assets/wallpaper1_1.jpg');
}

.service-card:nth-child(2) .service-image {
  background-image: url('@/assets/wallpaper1_2.jpg');
}

.service-card:nth-child(3) .service-image {
  background-image: url('@/assets/wallpaper1_3.jpg');
}

.service-image::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.5));
  opacity: 0.7;
  transition: opacity 0.4s ease;
}

.service-card:hover .service-image::after {
  opacity: 0.4;
}

.service-content {
  padding: 30px;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

.service-content h3 {
  font-size: 22px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  position: relative;
  padding-bottom: 15px;
}

.service-content h3::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 50px;
  height: 3px;
  background: linear-gradient(to right, #009688, #4CAF50);
}

.service-content p {
  font-size: 15px;
  line-height: 1.7;
  color: #666;
  margin-bottom: 20px;
  flex: 1;
}

.service-link {
  display: inline-flex;
  align-items: center;
  color: #009688;
  font-weight: 600;
  text-decoration: none;
  font-size: 15px;
  transition: all 0.3s ease;
}

.service-link i {
  margin-left: 8px;
  transition: transform 0.3s ease;
}

.service-link:hover {
  color: #00796b;
}

.service-link:hover i {
  transform: translateX(5px);
}

/* CTA部分样式 */
.cta-section {
  padding: 100px 0;
  background-image: url('@/assets/wallpaper1_4.jpg');
  background-size: cover;
  background-position: center;
  position: relative;
  color: #fff;
}

.cta-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(0, 77, 64, 0.9) 0%, rgba(0, 150, 136, 0.8) 100%);
}

.cta-content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

.cta-content h2 {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.cta-content p {
  font-size: 18px;
  line-height: 1.8;
  margin-bottom: 30px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.cta-btn {
  display: inline-block;
  padding: 15px 40px;
  background: #fff;
  color: #009688;
  text-decoration: none;
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.18);
}

.cta-btn:hover {
  background: #fff;
  transform: translateY(-4px);
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.28);
}

@media (max-width: 992px) {
  .services-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .highlights-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  .highlights-section {
    padding: 80px 0;
  }
}

@media (max-width: 768px) {
  .services-section,
  .cta-section,
  .highlights-section {
    padding: 70px 0;
  }
  
  .services-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .service-card {
    height: auto;
  }
  
  .cta-content h2 {
    font-size: 28px;
  }
  
  .cta-content p {
    font-size: 16px;
  }
  
  .section-title h2 {
    font-size: 24px;
  }
  
  .section-title p {
    font-size: 15px;
  }
}

@media (max-width: 576px) {
  .highlights-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .highlight-card {
    padding: 25px 15px;
  }
  
  .highlight-icon {
    width: 60px;
    height: 60px;
    font-size: 26px;
  }
  
  .highlight-card h3 {
    font-size: 18px;
  }
  
  .highlight-card p {
    font-size: 14px;
  }
}
</style> 